import { useState } from 'react'
import { Header } from './components/Header'
import { BrowseByStateStrip } from './components/BrowseByStateStrip'
import { SearchBar } from './components/SearchBar'
import { YourEventCard } from './components/YourEventCard'
import { RaceTimeHero, FeaturedHero } from './components/PromoHero'
import { MonthGroup } from './components/MonthGroup'
import { DesktopFilterRail } from './components/DesktopFilterRail'
import { MobileFilterSheet } from './components/MobileFilterSheet'
import { SponsorAds } from './components/SponsorAds'
import { DesktopToolbar, MobileChipsRow, type ViewMode } from './components/Toolbar'
import { activeFilterCount, DEFAULT_FILTERS, type FilterState, type Sort } from './data/filterState'
import { truthy } from './data/types'
import { useIsMobile } from './hooks/useIsMobile'
import { useLocalStorage } from './hooks/useLocalStorage'
import { useEventsListData } from './hooks/useEventsListData'
import toolbarStyles from './components/Toolbar.module.css'
import styles from './App.module.css'

export default function App() {
  const isMobile = useIsMobile()
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS)
  const [storedView, setStoredView] = useLocalStorage<ViewMode>('rcs-events-list-view', 'list')
  const [sheetOpen, setSheetOpen] = useState(false)
  const { data, error } = useEventsListData(filters, filters.sort)
  const setSort = (sort: Sort) => setFilters({ ...filters, sort })

  // Mobile has no Flyers option (Main.dc.html's segmented control is List/Calendar/Map only) -
  // the stored preference survives for whenever this viewer is back on desktop.
  const view: ViewMode = isMobile ? 'list' : storedView

  if (error) {
    return (
      <div className={styles.stateScreen}>
        <p>Couldn't load the events list.</p>
        <a href="/events/index.cfm">Try again</a>
      </div>
    )
  }

  if (!data) {
    return <div className={styles.stateScreen}>Loading…</div>
  }

  // Signing up requires being logged in as a racer (tracks/promoters can't enter races, and an
  // anonymous visitor would just be bounced to login from displayEvent.cfm) - every card's "Sign
  // up" CTA is dropped entirely rather than shown and failing later.
  const canSignUp = data.viewer.type === 'racer' && truthy(data.viewer.loggedIn)

  return (
    <div className={styles.page}>
      <Header viewer={data.viewer} />

      <SearchBar
        search={filters.search}
        onSearchChange={(v) => setFilters({ ...filters, search: v })}
        location={filters.location}
        radiusMi={filters.radiusMi}
        onLocationChange={(location, radiusMi) => setFilters({ ...filters, location, radiusMi })}
        when={filters.when}
        onWhenChange={(when) => setFilters({ ...filters, when })}
        totalCount={data.totalCount}
        isMobile={isMobile}
        onSubmit={() => {
          /* filters already apply live - Search is a no-op affordance matching the desktop mockup's submit button */
        }}
      />

      {isMobile ? (
        <MobileChipsRow
          activeCount={activeFilterCount(filters)}
          onOpenFilters={() => setSheetOpen(true)}
          filters={data.filters}
          selected={filters}
          onFilterChange={setFilters}
        />
      ) : null}

      <div className={styles.body}>
        {isMobile ? (
          <>
            {data.yourEvent ? (
              <section className={styles.stack}>
                <h2 className={styles.sectionLabel}>Your events</h2>
                <YourEventCard event={data.yourEvent} />
              </section>
            ) : null}

            <div className={styles.promoStack}>
              {data.raceTime.map((e) => (
                <RaceTimeHero key={e.id} event={e} canSignUp={canSignUp} />
              ))}
              {data.featured.map((e) => (
                <FeaturedHero key={e.id} event={e} canSignUp={canSignUp} />
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ViewAndSortMobile sort={filters.sort} onSortChange={setSort} />
            </div>

            <div className={styles.monthsStack}>
              {data.months.map((m) => (
                <MonthGroup key={m.name} month={m} isMobile view="list" canSignUp={canSignUp} />
              ))}
            </div>
          </>
        ) : (
          <div className={styles.desktopGrid}>
            <DesktopFilterRail filters={data.filters} selected={filters} onChange={setFilters} />

            <main className={styles.main}>
              {data.yourEvent ? <YourEventCard event={data.yourEvent} /> : null}
              {data.raceTime.map((e) => (
                <RaceTimeHero key={e.id} event={e} canSignUp={canSignUp} />
              ))}
              {data.featured.map((e) => (
                <FeaturedHero key={e.id} event={e} canSignUp={canSignUp} />
              ))}

              <DesktopToolbar
                totalCount={data.totalCount}
                filters={data.filters}
                selected={filters}
                onFilterChange={setFilters}
                sort={filters.sort}
                onSortChange={setSort}
                view={view}
                onViewChange={setStoredView}
              />

              <div className={styles.monthsStack}>
                {data.months.map((m) => (
                  <MonthGroup key={m.name} month={m} isMobile={false} view={view} canSignUp={canSignUp} />
                ))}
              </div>

              <PostEventCta />
            </main>
          </div>
        )}

        {isMobile ? (
          <>
            <PostEventCta />
            {/* DesktopFilterRail carries this same slot on desktop (left rail) - mobile has no
                rail at all (filters live in MobileFilterSheet instead, untouched here), so the
                ads just go at the bottom of the page. */}
            <SponsorAds />
          </>
        ) : null}
      </div>

      {isMobile && sheetOpen ? (
        <MobileFilterSheet
          filters={data.filters}
          selected={filters}
          pendingCount={data.totalCount}
          onChange={setFilters}
          onClose={() => setSheetOpen(false)}
        />
      ) : null}

      {/* Page-end footer band, not part of the primary search/filter/results flow above -
          Feature 164's internal links to the SEO /rc-races/{slug}/ region pages. Used to sit
          above the search bar (too prominent, competed with the actual search task); moved here
          per owner feedback 2026-10-12. */}
      <BrowseByStateStrip />
    </div>
  )
}

/** Mobile's view toggle (List/Calendar/Map, no Flyers) + Sort control, shown above the month
 *  list - Main.dc.html's row between the chip scroller and "Your events". */
function ViewAndSortMobile({ sort, onSortChange }: { sort: Sort; onSortChange: (sort: Sort) => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 8 }}>
      <ViewToggleMobileOnly />
      <SortButtonMobile sort={sort} onSortChange={onSortChange} />
    </div>
  )
}

function ViewToggleMobileOnly() {
  return (
    <div role="group" aria-label="View" className={toolbarStyles.mobileViewToggle}>
      <button type="button" aria-pressed="true" className={`${toolbarStyles.viewButton} ${toolbarStyles.viewButtonActive}`}>
        List
      </button>
      <button type="button" aria-pressed="false" disabled title="Coming soon" className={`${toolbarStyles.viewButton} ${toolbarStyles.viewButtonDisabled}`}>
        Calendar
      </button>
      <button type="button" aria-pressed="false" disabled title="Coming soon" className={`${toolbarStyles.viewButton} ${toolbarStyles.viewButtonDisabled}`}>
        Map
      </button>
    </div>
  )
}

function SortButtonMobile({ sort, onSortChange }: { sort: Sort; onSortChange: (sort: Sort) => void }) {
  return (
    <select
      aria-label="Sort"
      value={sort}
      onChange={(e) => onSortChange(e.target.value as Sort)}
      style={{ height: 44, border: 0, background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}
    >
      <option value="soonest">Soonest</option>
      <option value="nearest">Nearest</option>
      <option value="mostEntries">Most entries</option>
    </select>
  )
}

function PostEventCta() {
  return (
    <section className={toolbarStyles.postEventBox}>
      <div className={toolbarStyles.postEventTitle}>Run a track?</div>
      <div className={toolbarStyles.postEventBody}>List your race, take entries and payments online, and export straight to LiveTime.</div>
      <a href="/tracks/profile/addEditEvent.cfm" className={toolbarStyles.postEventLink}>
        Post an event
      </a>
    </section>
  )
}
